import { Check } from "lucide-react";
import { DEFAULT_CLASSIFICATION_TYPES } from "@/types/domain";
import { sportTypeIcons } from "@/lib/sport-icons";
import { getCourtColor } from "@/components/map/SportCourtMarker";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const SPORT_TYPE_LABELS: Record<string, string> = {
  soccer_pitch: "Soccer",
  tennis_court: "Tennis",
  padel_court: "Padel",
  basketball_court: "Basketball",
  volleyball_court: "Volleyball",
  running_track: "Running",
  baseball_diamond: "Baseball",
  rugby_pitch: "Rugby",
  field_hockey_pitch: "Field Hockey",
  cricket_pitch: "Cricket",
  skateboard_park: "Skateboard",
  multi_sport_court: "Multi-Sport",
  beach_volleyball_court: "Beach Volley",
  petanque_court: "Petanque",
  street_workout_area: "Workout",
  pickleball_court: "Pickleball",
  ping_pong_table: "Ping Pong",
};

interface CourtPlacementToolbarProps {
  armedSportType: string | null;
  onArm: (sportType: string) => void;
  onDisarm: () => void;
  onDone?: () => void;
}

export function CourtPlacementToolbar({
  armedSportType,
  onArm,
  onDisarm,
  onDone,
}: CourtPlacementToolbarProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1 flex-wrap justify-center">
        {onDone && (
          <Button size="sm" variant="outline" onClick={onDone} className="mr-2 shrink-0">
            <Check className="size-3.5 mr-1" /> Done
          </Button>
        )}
        {DEFAULT_CLASSIFICATION_TYPES.map((type) => {
          const Icon = sportTypeIcons[type];
          const isArmed = armedSportType === type;
          const color = getCourtColor(type);

          return (
            <Tooltip key={type}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center rounded-full border-2 transition-all duration-150"
                  style={{
                    width: isArmed ? 36 : 30,
                    height: isArmed ? 36 : 30,
                    backgroundColor: isArmed ? color : "rgba(255,255,255,0.9)",
                    borderColor: isArmed ? "white" : color,
                    transform: isArmed ? "scale(1.1)" : "scale(1)",
                    boxShadow: isArmed ? `0 0 0 3px ${color}40` : "none",
                  }}
                  onClick={() => {
                    if (isArmed) {
                      onDisarm();
                    } else {
                      onArm(type);
                    }
                  }}
                >
                  {Icon && (
                    <Icon
                      size={isArmed ? 18 : 15}
                      stroke={1.5}
                      color={isArmed ? "white" : color}
                    />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {SPORT_TYPE_LABELS[type] ?? type}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
